import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory } from 'react-router-dom';
import routes from '../../../constants/routes';
import { findContactDataByDeal } from '../../../services';
import { extractErrorMessage, isOnlyDigit } from '../helpers';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { AutocompleteContactData } from '../types';
import { copyQuotation } from '../services';
import GeneralConfigForm from './GeneralConfig/GeneralConfigForm';
import { getNimbuxCaseSummary } from '../../common/services';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

interface Props {
	quotationIdToCopy: number;
	isOnlyFlights: boolean;
	open: boolean;
	onClose: () => void;
	onSuccessCopyQuotation: (newId: number) => void;
}

export const CopyQuotationDialog = (props: Props): JSX.Element => {
	const [email, setEmail] = React.useState('');
	const [name, setName] = React.useState('');
	const [phone, setPhone] = React.useState('');
	const [crmTicket, setCrmTicket] = React.useState(0);
	const [isBitrixDeal, setIsBitrixDeal] = React.useState(true);
	const [caseClientId, setCaseClientId] = useState<string | null>(null);
	const [description, setDescription] = React.useState('');
	const [errorMessageToShow, setErrorMessageToShow] = React.useState('');

	const [loading, setLoading] = React.useState(false);
	const classes = useStyles();
	const history = useHistory();

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value);

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value);

	const handleCRMTicketChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isOnlyDigit(e.target.value) && Number(e.target.value) > 0) setCrmTicket(Number(e.target.value));
	};
	const handleChangeIsBitrixDeal = (value: boolean) => {
		clearContactData();
		setIsBitrixDeal(value);
		setCrmTicket(0);
	};

	const clearContactData = () => {
		setName('');
		setPhone('');
		setEmail('');
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value);

	const setAutoCompleteContactData = (payload: AutocompleteContactData) => {
		setName(payload.name);
		setPhone(payload.phone || '');
		setEmail(payload.email || '');
	};

	const handleAutoCompleteNameEmailAndPhone = async () => {
		try {
			setLoading(true);
			setCaseClientId(null);
			if (isBitrixDeal) {
				const response = await findContactDataByDeal(crmTicket);
				setAutoCompleteContactData(response.data);
			} else {
				const response = await getNimbuxCaseSummary(crmTicket);
				if (!response.data?.client) {
					setErrorMessageToShow('El caso no tiene cliente asociado, por favor asocie un cliente al caso');
					clearContactData();
				} else {
					const client = response.data.client;
					setCaseClientId(client.id);
					if (!client.email && !client.whatsappNumber) {
						setErrorMessageToShow(
							'Por favor complete el email o el número de whatsaapp del cliente en el CRM de Mevuelo.',
						);
						clearContactData();
					} else {
						const autocompleteContactData = {
							name: `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim(),
							email: client.email || '',
							phone: client.whatsappNumber || '',
						} as AutocompleteContactData;
						setAutoCompleteContactData(autocompleteContactData);
					}
				}
			}

			setLoading(false);
		} catch (error) {
			setErrorMessageToShow(extractErrorMessage(error, 'No se pudo obtener los datos de contacto'));
			setLoading(false);
		}
	};

	const handleConfirmAutoCompleteContactDataByDeal = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && crmTicket > 0) {
			handleAutoCompleteNameEmailAndPhone();
		}
	};

	const handleCopyQuotation = async () => {
		if (isValid()) {
			try {
				setLoading(true);
				const payload = {
					quotationIdToCopy: props.quotationIdToCopy,
					isOnlyFlights: props.isOnlyFlights,
					crmTicket,
					isBitrixDeal,
					description,
					name,
					email: email && email != '' ? email : null,
					phone,
				};
				const result = await copyQuotation(payload);
				setLoading(false);

				// Navegar a la nueva cotización en la misma pestaña
				const newQuotationId = result.data;
				const route = props.isOnlyFlights
					? routes.quotations.editOnlyFlights.replace(':id', String(newQuotationId))
					: routes.quotations.edit.replace(':id', String(newQuotationId));

				props.onSuccessCopyQuotation(result.data);

				// Navegar después de un breve delay para que se vea el mensaje de éxito
				setTimeout(() => {
					history.push(route);
				}, 500);
			} catch (error) {
				setErrorMessageToShow(extractErrorMessage(error, 'Ocurrió un error al copiar la cotización'));
				setLoading(false);
			}
		}
	};

	const isValidName = () => name && name.length > 0 && name.length <= 500 && name.replace(/ /g, '').length > 0;
	const isValidDeal = () => crmTicket > 0;
	const isValid = () => isValidName() && isValidDeal();
	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Snackbar open={errorMessageToShow.length > 0} autoHideDuration={2000} onClose={() => setErrorMessageToShow('')}>
				<Alert variant="filled" severity={'error'}>
					{errorMessageToShow}
				</Alert>
			</Snackbar>
			<Dialog
				open={props.open}
				onClose={props.onClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				fullWidth
				maxWidth="md"
			>
				<DialogTitle id="alert-dialog-title">{'Copiar cotización'}</DialogTitle>
				<DialogContent>
					<Typography component="h1" variant="h6">
						Inserte los datos de la nueva cotización
					</Typography>
					<GeneralConfigForm
						isBitrixDeal={isBitrixDeal}
						onIsBitrixDealChange={handleChangeIsBitrixDeal}
						onEmailChange={handleEmailChange}
						onCRMTicketChange={handleCRMTicketChange}
						onPhoneChange={handlePhoneChange}
						onNameChange={handleNameChange}
						onConfirmAutoCompleteDataByDeal={handleConfirmAutoCompleteContactDataByDeal}
						crmTicket={crmTicket}
						name={name}
						email={email}
						phone={phone}
						descripcion={description}
						onDescriptionChange={handleDescriptionChange}
						showDescriptionField={true}
						caseClientId={caseClientId}
					/>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						Cancelar
					</Button>
					<Button variant="contained" color="primary" disabled={!isValid()} onClick={handleCopyQuotation} autoFocus>
						Copiar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
